import React, { useState, useEffect } from "react";
import LZString from "lz-string";
import "./checkStock.css";
import StockSearchTable from './stockSearchTable';
import Label from './label';
import PO from './po';
import Pricing from './pricing';
import StockStats from './stock-stats';
import RawCosts from './rawCosts';
import SalesAnalytics from './salesAnalytics';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const CheckStock = () => {
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [viewMode, setViewMode] = useState('stock');
    const [stats, setStats] = useState({
        total_items: 0,
        total_stock: 0,
        pages_processed: 0
    });
    const [loadingProgress, setLoadingProgress] = useState({
        current: '',
        processed: 0,
        total: 0
    });
    const [variantSales, setVariantSales] = useState({});
    const [coreProducts, setCoreProducts] = useState({});

    // Fetch core products on mount
    useEffect(() => {
        fetchCoreProducts();
    }, []);  // Empty dependency array means it runs once on mount

    const fetchCoreProducts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/core-products`);
            if (response.ok) {
                const data = await response.json();
                setCoreProducts(data.core_products);
            }
        } catch (error) {
            console.error("Error fetching core products:", error);
        }
    };

    const addToCore = async (product) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/core-products/${product.product_id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sku: product.variants[0]?.sku || '',
                    title: product.product_title
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Update local state immediately after successful API call
            setCoreProducts(prev => ({
                ...prev,
                [product.product_id]: {
                    sku: product.variants[0]?.sku || '',
                    title: product.product_title
                }
            }));
        } catch (error) {
            console.error("Error adding to core:", error);
        }
    };

    const removeFromCore = async (product) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/core-products/${product.product_id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Update local state immediately after successful API call
            setCoreProducts(prev => {
                const newState = { ...prev };
                delete newState[product.product_id];
                return newState;
            });
        } catch (error) {
            console.error("Error removing from core:", error);
        }
    };

    const fetchAllInventory = async () => {
        setLoading(true);
        setError(null);
        setLoadingProgress({ 
            current: 'Starting inventory fetch...', 
            processed: 0, 
            total: 250
        });

        try {
            const response = await fetch(`${API_BASE_URL}/api/get-all-inventory`);
            if (!response.ok) {
                throw new Error('Failed to fetch inventory');
            }
            const data = await response.json();
            
            console.log('Fetched inventory data:', data);

            // Save the inventory data in session storage
            sessionStorage.setItem('inventoryData', JSON.stringify(data.inventory || []));

            // Update state with the fetched data
            setInventory(data.inventory || []);

            setStats({
                total_items: data.total_items,
                pages_processed: data.pages_processed,
                total_stock: data.inventory.reduce((sum, product) => 
                    sum + product.variants.reduce((variantSum, variant) => 
                        variantSum + (variant.inventory_quantity || 0), 0), 0)
            });

            // Then fetch sales data
            setLoadingProgress({ 
                current: 'Fetching sales data...', 
                processed: data.total_items, 
                total: data.total_items 
            });

            // Get compressed orders data from session storage
            const compressedData = sessionStorage.getItem('rawOrdersData');
            if (compressedData) {
                const decompressedData = JSON.parse(LZString.decompress(compressedData));
                let allSales = {};
                
                decompressedData.forEach(order => {
                    if (order.line_items) {
                        order.line_items.forEach(item => {
                            const salesKey = `${item.product_id}-${item.variant_id}`;
                            const quantity = parseInt(item.quantity) || 0;
                            allSales[salesKey] = (allSales[salesKey] || 0) + quantity;
                        });
                    }
                });
                
                setVariantSales(allSales);
            }

            // Finally fetch core products
            setLoadingProgress({ 
                current: 'Fetching core products...', 
                processed: data.total_items, 
                total: data.total_items 
            });

            const coreResponse = await fetch(`${API_BASE_URL}/api/core-products`);
            if (coreResponse.ok) {
                const coreData = await coreResponse.json();
                
                // Store the core products response in session storage
                sessionStorage.setItem('coreProductsData', JSON.stringify(coreData.core_products || []));
                
                setCoreProducts(coreData.core_products);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setTimeout(() => {
                setLoading(false);
                setLoadingProgress({ current: '', processed: 0, total: 0 });
            }, 1000);
        }
    };

    return (
        <div className="main-container">
            <div className="view-controls">
                <nav className="view-nav">
                    <button 
                        className={`view-tab ${viewMode === 'stock' ? 'active' : ''}`}
                        onClick={() => setViewMode('stock')}
                    >
                        Overall
                    </button>
                    <button 
                        className={`view-tab ${viewMode === 'po' ? 'active' : ''}`}
                        onClick={() => setViewMode('po')}
                    >
                        PO
                    </button>
                    <button 
                        className={`view-tab ${viewMode === 'pricing' ? 'active' : ''}`}
                        onClick={() => setViewMode('pricing')}
                    >
                        Pricing
                    </button>
                    <button 
                        className={`view-tab ${viewMode === 'label' ? 'active' : ''}`}
                        onClick={() => setViewMode('label')}
                    >
                        Label
                    </button>
                    <button 
                        className={`view-tab ${viewMode === 'stats' ? 'active' : ''}`}
                        onClick={() => setViewMode('stats')}
                    >
                        Statistics
                    </button>
                    <button 
                        className={`view-tab ${viewMode === 'costs' ? 'active' : ''}`}
                        onClick={() => setViewMode('costs')}
                    >
                        Costs
                    </button>
                    <button 
                        className={`view-tab ${viewMode === 'analytics' ? 'active' : ''}`}
                        onClick={() => setViewMode('analytics')}
                    >
                        Sales Analytics
                    </button>
                </nav>
            </div>

            {viewMode === 'stock' ? (
                <div className="check-stock-container">
                    <div className="stock-container">
                        <h2>Inventory Check</h2>
                        
                        <div className="controls">
                            <button 
                                onClick={fetchAllInventory} 
                                disabled={loading}
                                className="fetch-button"
                            >
                                {loading ? "Loading..." : "Fetch All Inventory"}
                            </button>

                            {loading && (
                                <div className="loading-status">
                                    <div className="loading-text">{loadingProgress.current}</div>
                                    {loadingProgress.total > 0 && (
                                        <div className="progress-bar-container">
                                            <div 
                                                className="progress-bar"
                                                style={{
                                                    width: `${(loadingProgress.processed / loadingProgress.total) * 100}%`
                                                }}
                                            />
                                            <span className="progress-text">
                                                {loadingProgress.processed} / {loadingProgress.total}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {stats.total_items > 0 && !loading && (
                                <div className="stats">
                                    <p>Total Products: {stats.total_items}</p>
                                    <p>Total Stock: {stats.total_stock}</p>
                                    <p>Pages Processed: {stats.pages_processed}</p>
                                </div>
                            )}
                        </div>
                        
                        {error ? (
                            <div className="error">{error}</div>
                        ) : (
                            <StockSearchTable 
                                inventory={inventory}
                                FIXED_VARIANTS={[]}
                                coreProducts={coreProducts}
                                addToCore={addToCore}
                                removeFromCore={removeFromCore}
                                variantSales={variantSales}
                            />
                        )}
                    </div>
                </div>
            ) : viewMode === 'po' ? (
                <PO 
                    inventory={inventory}
                    coreProducts={coreProducts}
                    variantSales={variantSales}
                    orders={JSON.parse(LZString.decompress(sessionStorage.getItem('rawOrdersData')) || '[]')}
                />
            ) : viewMode === 'pricing' ? (
                <Pricing 
                    inventory={inventory}
                    coreProducts={coreProducts}
                />
            ) : viewMode === 'label' ? (
                <Label inventory={inventory} />
            ) : viewMode === 'stats' ? (
                <StockStats />
            ) : viewMode === 'costs' ? (
                <RawCosts 
                    inventory={inventory} 
                    variantSales={variantSales}
                />
            ) : viewMode === 'analytics' ? (
                <SalesAnalytics 
                    inventory={inventory} 
                    variantSales={variantSales}
                    coreProducts={coreProducts}
                />
            ) : null}
        </div>
    );
};

export default CheckStock;
